/**
 * Schema Converter
 *
 * 将 TdsFormItemSchema 转换为 TdsFormItem，支持两种字符串格式：
 *
 * 1. 模板字符串格式：{{ expression }}
 *    用于执行 JavaScript 表达式，可以调用注册表中的函数和常量
 *    示例：{{ iconTextHtml('<span>提示内容<a href="...">链接</a></span>') }}
 *
 * 2. HTML 字符串格式：<tag>...</tag>
 *    自动检测并解析为 ReactNode，使用 dangerouslySetInnerHTML 渲染
 *    示例：<span>这是 HTML 内容<a href="...">链接</a></span>
 *
 * 注意：
 * - HTML 字符串会被解析为 ReactNode，但不会自动添加图标
 * - 如果需要带图标的提示，请使用模板字符串：{{ iconTextHtml('<span>...</span>') }}
 * - iconTextHtml 是 iconText 的 HTML 版本，接受 HTML 字符串参数
 */
import { set } from 'lodash-es';
import {
  deepMerge,
  isPlainObject,
  parseHtmlString,
  parseTemplateStringsRecursively,
} from '@/infrastructure/template-parser';
import { iconText } from '@/presentation/components/hint-text/hintText';
import { Zone } from '@/proto/field';
import { TdsFormItem } from '../form';
import { customFields2Filter, customFilter } from '../utils';
import { FieldReaction, TdsFormItemSchema, SchemaConverterOptions } from './schema';
import { STANDARD_FIELD_POOL } from './standard-field-pool';

/**
 * iconTextHtml: 接受 HTML 字符串，渲染为带图标的 Tooltip
 * 这是 iconText 的 HTML 版本，可以直接使用 HTML 字符串而不需要引用常量
 *
 * @example
 * iconTextHtml('<span>这是提示内容，支持<a href="...">链接</a></span>')
 * iconTextHtml('<span>字符串类型筛选项，支持精确搜索和模糊搜索，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>')
 */
function iconTextHtml(
  htmlContent: string,
  placement?: Parameters<typeof iconText>[1],
  onVisibleChange?: Parameters<typeof iconText>[2],
) {
  const htmlNode = parseHtmlString(htmlContent);
  return iconText(htmlNode, placement, onVisibleChange);
}

/**
 * 统一的注册表：包含所有可用的函数、组件和常量
 * 用于在模板字符串中引用
 */
const REGISTRY: Record<string, any> = {
  // 函数
  customFields2Filter: customFields2Filter as any,
  customFilter: customFilter as any,
  // 组件函数
  iconTextHtml,
};

/**
 * 检查是否是 FieldReaction 对象
 * FieldReaction 有 dependencies 和 fulfill 属性
 */
function isFieldReaction(value: any): value is FieldReaction {
  return (
    value
    && typeof value === 'object'
    && Array.isArray(value.dependencies)
    && value.fulfill
    && typeof value.fulfill === 'object'
  );
}

/**
 * 将 Schema 转换为 TdsFormItem
 */
export function schemaToFormItem(
  schema: TdsFormItemSchema,
  options?: SchemaConverterOptions,
): TdsFormItem {
  // Simple 模式：从基础字段池查找
  if (schema.schemaType === 'simple') {
    const baseConfig = STANDARD_FIELD_POOL[schema.name];
    if (!baseConfig) {
      throw new Error(`Field ${schema.name} not found in standard field pool`);
    }

    // 合并覆盖配置
    const result: TdsFormItem = {
      ...baseConfig,
    };

    // 应用覆盖配置，对所有 object 类型的值进行深度合并
    if (schema.overrides) {
      Object.keys(schema.overrides).forEach((key) => {
        const overrideValue = schema.overrides![key as keyof typeof schema.overrides];
        const currentValue = result[key as keyof typeof result];

        // 如果覆盖值是普通对象，进行深度合并
        if (isPlainObject(overrideValue)) {
          // 如果当前值也是普通对象，进行深度合并
          if (isPlainObject(currentValue)) {
            (result as any)[key] = deepMerge(currentValue, overrideValue);
          } else {
            // 否则直接使用覆盖值
            (result as any)[key] = overrideValue;
          }
        } else {
          // 非普通对象类型直接赋值
          (result as any)[key] = overrideValue;
        }
      });
    }

    // 递归解析所有模板字符串（包括嵌套在 fieldProps、overrides 等中的）
    const parsedResult = parseTemplateStringsRecursively(
      result,
      REGISTRY,
      new WeakSet(),
      true,
      isFieldReaction,
    );
    // 处理需要动态注入的属性（如 fieldScope）
    // 只有当 fieldScope 不存在时才使用 options.zone，避免覆盖 overrides 中的设置
    // 使用 lodash 的 set 方法安全地设置嵌套路径，即使中间路径不存在也会自动创建
    if (options?.zone && !parsedResult.fieldProps?.valueSelectProps?.fieldScope) {
      set(parsedResult, 'fieldProps.valueSelectProps.fieldScope', options.zone as Zone);
    }

    // 保留 reactions 配置（reactions 中的表达式字符串不会被解析，需要在运行时由 reaction-engine 解析）
    if (schema.reactions) {
      (parsedResult as any).reactions = schema.reactions;
    }

    return parsedResult;
  }

  // Full 模式：直接转换，递归解析所有模板字符串
  const baseResult: any = {
    ...schema,
    type: schema.type,
    filterType: schema.filterType,
    group: schema.group,
  };

  // 递归解析所有模板字符串（包括 filterTransform、convertValue、transform、labelTools、batchEditTips、fieldProps、reactions 等）
  const fullResult: TdsFormItem = parseTemplateStringsRecursively(
    baseResult,
    REGISTRY,
    new WeakSet(),
    true,
    isFieldReaction,
  );

  // 处理动态属性注入
  // 只有当 fieldScope 不存在时才使用 options.zone，避免覆盖 schema 中的设置
  // 使用 lodash 的 set 方法安全地设置嵌套路径，即使中间路径不存在也会自动创建
  if (options?.zone && !(fullResult.fieldProps as any)?.valueSelectProps?.fieldScope) {
    set(fullResult, 'fieldProps.valueSelectProps.fieldScope', options.zone as Zone);
  }

  return fullResult;
}
